import { AlertCircle, Hand, Info, Route, Sparkles } from 'lucide-react';
import { RouteStatus } from '@certhon/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/Badge/badge';
import { Button } from '@/components/ui/button';

interface RouteStatusBadgeProps {
  /** Current route status */
  status: RouteStatus;
  /** Whether the current user can trigger auto-sort */
  canAutoSort: boolean;
  /** Whether the current user can make manual changes (admin) */
  canManualEdit: boolean;
  /** Callback when user clicks recalculate/optimize */
  onOptimize: () => void;
  /** Whether optimization is currently in progress */
  isOptimizing?: boolean;
  /** Timestamp when route was last calculated */
  routingTimestamp?: Date | null;
}

/**
 * Display route status badge/alert for day planning
 *
 * Shows different states:
 * -  auto-calculated
 * - auto_stale: unsorted: Warning with optimize button
 * - auto_valid: Simple badge showingWarning with recalculate button
 * - manual_valid: Simple badge showing manual
 * - manual_stale: Info notice about changes
 */
export function RouteStatusBadge({
  status,
  canAutoSort,
  canManualEdit,
  onOptimize,
  isOptimizing,
  routingTimestamp,
}: RouteStatusBadgeProps) {
  // Unsorted (NULL): Show subtle loading when auto-optimizing, or prompt if manual action needed
  if (status === 'unsorted') {
    // If already optimizing, show subtle indicator
    if (isOptimizing) {
      return (
        <Badge variant="secondary" className="gap-1">
          <Route className="h-3 w-3 animate-pulse" />
          Calculating optimal route...
        </Badge>
      );
    }

    // Only show alert if not auto-optimizing (shouldn't happen normally)
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Route not sorted</AlertTitle>
        <AlertDescription>
          This route needs to be sorted. Click below to auto-calculate optimal
          order.
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={onOptimize}
              disabled={isOptimizing}
            >
              <Route className="mr-1 h-4 w-4" />
              Auto-sort route
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Auto route, no changes: Show "Auto-calculated" badge
  if (status === 'auto_valid') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Sparkles className="h-3 w-3" />
        Auto-calculated route
        {routingTimestamp && (
          <span className="text-muted-foreground ml-1 text-xs">
            ({formatTimestamp(routingTimestamp)})
          </span>
        )}
      </Badge>
    );
  }

  // Auto route with changes: Show warning with recalculate button
  if (status === 'auto_stale') {
    // If already optimizing (or about to auto-optimize), show subtle indicator
    if (isOptimizing) {
      return (
        <Badge variant="secondary" className="gap-1">
          <Route className="h-3 w-3 animate-pulse" />
          Recalculating route...
        </Badge>
      );
    }

    return (
      <Alert className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Workorders changed</AlertTitle>
        <AlertDescription>
          The workorder set has changed since the route was calculated.
          <div className="mt-2 flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={onOptimize}
              disabled={isOptimizing || !canAutoSort}
            >
              <Route className="mr-1 h-4 w-4" />
              {isOptimizing ? 'Optimizing...' : 'Recalculate optimal route'}
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Manual route with changes: Show informational warning
  if (status === 'manual_stale') {
    return (
      <Alert className="mb-4">
        <Info className="h-4 w-4" />
        <AlertTitle>Manual route - workorders changed</AlertTitle>
        <AlertDescription>
          The workorder set has changed since this route was manually arranged.
          {canManualEdit ? (
            <div className="mt-2 flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={onOptimize}
                disabled={isOptimizing}
              >
                <Route className="mr-1 h-4 w-4" />
                {isOptimizing ? 'Optimizing...' : 'Recalculate optimal route'}
              </Button>
            </div>
          ) : (
            <p className="text-muted-foreground mt-2 text-sm">
              Contact an admin to update the route.
            </p>
          )}
        </AlertDescription>
      </Alert>
    );
  }

  // Manual route, no changes: Show badge with option to switch to auto
  if (status === 'manual_valid') {
    return (
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="gap-1">
          <Hand className="h-3 w-3" />
          Manual route
          {routingTimestamp && (
            <span className="text-muted-foreground ml-1 text-xs">
              ({formatTimestamp(routingTimestamp)})
            </span>
          )}
        </Badge>
        {canManualEdit && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onOptimize}
            disabled={isOptimizing}
            title="Switch to auto-calculated optimal route"
          >
            <Route className="mr-1 h-4 w-4" />
            {isOptimizing ? 'Optimizing...' : 'Switch to auto'}
          </Button>
        )}
      </div>
    );
  }

  return null;
}

// FIXME: use existing utils or date-fns
/**
 * Format timestamp for display
 */
function formatTimestamp(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

/**
 * Compact version of RouteStatusBadge for toolbars
 */
export function RouteStatusBadgeCompact({
  status,
  canAutoSort,
  onOptimize,
  isOptimizing,
}: Pick<
  RouteStatusBadgeProps,
  'status' | 'canAutoSort' | 'onOptimize' | 'isOptimizing'
>) {
  // For unsorted or stale routes, show the optimize button
  if (status === 'unsorted' || status === 'auto_stale') {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={onOptimize}
        disabled={isOptimizing || !canAutoSort}
        title={
          status === 'unsorted'
            ? 'Route not sorted - click to optimize'
            : 'Workorders changed - click to recalculate'
        }
      >
        <Route className="mr-1 h-4 w-4" />
        {isOptimizing ? 'Optimizing...' : 'Optimize route'}
      </Button>
    );
  }

  // For valid routes, show a simple badge
  if (status === 'auto_valid') {
    return (
      <Badge variant="secondary" className="gap-1">
        <Sparkles className="h-3 w-3" />
        Auto
      </Badge>
    );
  }

  if (status === 'manual_valid' || status === 'manual_stale') {
    return (
      <Badge
        variant={status === 'manual_stale' ? 'destructive' : 'outline'}
        className="gap-1"
      >
        <Hand className="h-3 w-3" />
        Manual
        {status === 'manual_stale' && ' (stale)'}
      </Badge>
    );
  }

  return null;
}
